/**
 * Quick actions on an accounts table row: log a call or a comment, or start a deal, without
 * opening the account. Logging reuses the Calls tab form.
 */
import { Handshake, MoreHorizontal, NotebookPen } from "lucide-react";
import { useGetList } from "ra-core";
import { useState } from "react";
import { Link } from "react-router";

import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

import { LogActivityForm } from "../calls/LogActivityForm";
import type { ZcrmAccount } from "../providers/zinspector/accounts";
import type { Contact } from "../types";

const stop = (event: { stopPropagation: () => void }) =>
  event.stopPropagation();

export const AccountRowActions = ({ account }: { account: ZcrmAccount }) => {
  const [logging, setLogging] = useState(false);
  return (
    <span onClick={stop}>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button
            variant="ghost"
            size="icon"
            className="size-7"
            aria-label={`Actions for ${account.name}`}
          >
            <MoreHorizontal />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" onClick={stop}>
          <DropdownMenuItem onSelect={() => setLogging(true)}>
            <NotebookPen /> Log a call or comment
          </DropdownMenuItem>
          <DropdownMenuItem asChild>
            <Link to={`/deals/create?company_id=${account.id}`}>
              <Handshake /> New deal
            </Link>
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
      {logging ? (
        <QuickLogDialog account={account} onClose={() => setLogging(false)} />
      ) : null}
    </span>
  );
};

const QuickLogDialog = ({
  account,
  onClose,
}: {
  account: ZcrmAccount;
  onClose: () => void;
}) => {
  const { data: contacts = [] } = useGetList<Contact>("contacts", {
    filter: { company_id: account.id },
    pagination: { page: 1, perPage: 500 },
    sort: { field: "last_name", order: "ASC" },
  });
  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="sm:max-w-3xl" onClick={stop}>
        <DialogHeader>
          <DialogTitle>Log for {account.name}</DialogTitle>
        </DialogHeader>
        <LogActivityForm
          companyId={account.id}
          accountName={account.name}
          contacts={contacts.map((c) => ({
            id: c.id,
            name: `${c.first_name} ${c.last_name}`.trim(),
          }))}
          onLogged={onClose}
        />
      </DialogContent>
    </Dialog>
  );
};
